import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Zap, Gift, Trophy, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface RewardCelebrationProps {
    isOpen: boolean;
    onClose: () => void;
    xpEarned?: number;
    creditsEarned?: number;
    leveledUp?: boolean;
    newLevel?: number;
    taskTitle?: string;
}

const confettiColors = ['#FFD700', '#00E676', '#2196F3', '#E040FB', '#FF5722'];

function ConfettiPiece({ delay, color }: { delay: number; color: string }) {
    const randomX = Math.random() * 100;
    const randomRotate = Math.random() * 360;

    return (
        <motion.div
            initial={{
                y: -20,
                x: `${randomX}vw`,
                rotate: 0,
                opacity: 1
            }}
            animate={{
                y: '100vh',
                rotate: randomRotate * 3,
                opacity: 0
            }}
            transition={{
                duration: 2 + Math.random(),
                delay,
                ease: 'easeIn'
            }}
            className="fixed top-0 pointer-events-none z-50"
            style={{
                width: 8 + Math.random() * 8,
                height: 8 + Math.random() * 8,
                backgroundColor: color,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            }}
        />
    );
}

export function RewardCelebration({
    isOpen,
    onClose,
    xpEarned = 0,
    creditsEarned = 0,
    leveledUp = false,
    newLevel,
    taskTitle = 'Task Completa!',
}: RewardCelebrationProps) {
    const [confetti, setConfetti] = useState<{ id: number; delay: number; color: string }[]>([]);

    useEffect(() => {
        if (isOpen) {
            // Generate confetti pieces
            const pieces = Array.from({ length: 50 }, (_, i) => ({
                id: i,
                delay: Math.random() * 0.5,
                color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
            }));
            setConfetti(pieces);

            // Auto-close after 3 seconds
            const timer = setTimeout(onClose, 3500);
            return () => clearTimeout(timer);
        }
    }, [isOpen, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Confetti */}
                    {confetti.map((piece) => (
                        <ConfettiPiece key={piece.id} delay={piece.delay} color={piece.color} />
                    ))}

                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={onClose}
                    >
                        {/* Modal */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 10 }}
                            transition={{ type: 'spring', damping: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-card border border-border rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl"
                        >
                            {/* Icon */}
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: [0, 1.2, 1] }}
                                transition={{ delay: 0.1, duration: 0.5 }}
                                className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center"
                            >
                                {leveledUp ? (
                                    <Trophy className="w-10 h-10 text-amber-500" />
                                ) : creditsEarned > 0 ? (
                                    <Gift className="w-10 h-10 text-emerald-500" />
                                ) : (
                                    <Sparkles className="w-10 h-10 text-primary" />
                                )}
                            </motion.div>

                            {/* Title */}
                            <motion.h2
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="text-2xl font-bold mb-2"
                            >
                                {leveledUp ? '🎉 Level Up!' : '✨ Parabéns!'}
                            </motion.h2>

                            {/* Task Title */}
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                className="text-muted-foreground mb-6"
                            >
                                {taskTitle}
                            </motion.p>

                            {/* Rewards */}
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="flex items-center justify-center gap-6 mb-6"
                            >
                                {xpEarned > 0 && (
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-5 h-5 text-amber-500" />
                                        <span className="text-xl font-bold text-amber-500">+{xpEarned} XP</span>
                                    </div>
                                )}
                                {creditsEarned > 0 && (
                                    <div className="flex items-center gap-2">
                                        <Gift className="w-5 h-5 text-emerald-500" />
                                        <span className="text-xl font-bold text-emerald-500">
                                            +{creditsEarned} crédito{creditsEarned > 1 ? 's' : ''}
                                        </span>
                                    </div>
                                )}
                            </motion.div>

                            {/* Level Up Badge */}
                            {leveledUp && newLevel && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.5, type: 'spring' }}
                                    className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-lg p-3 mb-6"
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <Star className="w-5 h-5 text-amber-500" />
                                        <span className="text-lg font-bold">Nível {newLevel}!</span>
                                        <Star className="w-5 h-5 text-amber-500" />
                                    </div>
                                </motion.div>
                            )}

                            {/* Close Button */}
                            <Button onClick={onClose} className="w-full" variant="hero">
                                Continuar
                            </Button>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
